import i18n from 'helpers/i18n';
import messages from './messages';
import reliabilityMessages from '../messages';
import { useEffect, useRef, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import SubmitScoreSection from '../SubmitScoreSection';
import DimensionScoreInput from '../DimensionScoreInput';
import IncompleteDimensions from '../IncompleteDimensions';
import { SectionStatus } from 'components/SectionCollapse/SectionStatus';
import {
  getUserAnswer,
  createUserScore,
  refreshUserScores,
  getMissingDimensions
} from './utils';
import { areAllCodesCompleted, showIncompleteBorder } from '../utils';
import { Dimension } from 'types/api/TestAttempt';

type DimensionScore = {
  id: number;
  answer: number;
  dimensionId: number;
};

interface EnterCodesSectionProps {
  testPreparationId: string;
  dimensions: Dimension[];
  videoId: string | number;
  testType: 'reliability' | 'testPreparations';
  setShowConfirmationModal: (value: boolean) => void;
  updateUserScores?: (value: DimensionScore[]) => void;
}

function EnterCodesSection({
  dimensions,
  testPreparationId,
  videoId,
  testType,
  setShowConfirmationModal,
  updateUserScores
}: EnterCodesSectionProps) {
  const [dimensionsCount, setDimensionsCount] = useState(dimensions.length);
  const [userScores, setUserScores] = useState<DimensionScore[]>([]);
  const [completeCodesError, setCompleteCodesError] = useState(false);
  const isTablet = useMediaQuery({ minWidth: 768 });
  const dimensionRefs = useRef<Record<number, HTMLFieldSetElement | null>>({});
  const missingDimensions = getMissingDimensions(dimensions, userScores);
  const missingDimensionsList = missingDimensions
    .map(dimension => `${dimension.name} (${dimension.abbreviation})`)
    .join(', ');
  const sectionStatus = completeCodesError
    ? 'error'
    : areAllCodesCompleted(dimensionsCount, userScores.length)
      ? 'completed'
      : 'pending';

  function getMissingDimensionError(dimension: Dimension) {
    return i18n.ft(reliabilityMessages.dimensionCodeError, {
      name: dimension.name,
      abbreviation: dimension.abbreviation
    });
  }

  useEffect(() => {
    if (!dimensions) {
      return;
    }

    let loadPreviousScores: DimensionScore[] = [];
    setDimensionsCount(dimensions.length);
    dimensions.forEach((dimension: Dimension) => {
      if (dimension.user_answer)
        loadPreviousScores.push({
          id: dimension.user_answer.id,
          answer: dimension.user_answer.answer,
          dimensionId: dimension.id
        });
    });
    setUserScores(loadPreviousScores);
  }, [dimensions]);

  function addUserScore(userScoreValue: DimensionScore) {
    let updateUserScores = [...userScores];

    if (createUserScore(updateUserScores, userScoreValue)) {
      updateUserScores.push(userScoreValue);
      setUserScores(prevUserScores => [...prevUserScores, userScoreValue]);
    } else {
      setUserScores(prevUserScores =>
        refreshUserScores(prevUserScores, userScoreValue)
      );
    }

    if (
      updateUserScores.length === dimensionsCount ||
      dimensionsCount - updateUserScores.length > 3
    ) {
      setCompleteCodesError(false);
    }
  }

  function verifySubmission() {
    if (updateUserScores) {
      updateUserScores(userScores);
    }

    if (areAllCodesCompleted(dimensionsCount, userScores.length)) {
      setShowConfirmationModal(true);
    } else {
      setCompleteCodesError(true);
      dimensionRefs.current[missingDimensions[0]?.id]?.focus();
    }
  }

  return (
    <>
      <div className="mt-2 lg:mx-8">
        <section
          aria-labelledby="enter-codes-title"
          className={
            completeCodesError ? 'border border-ds-border-control-negative' : ''
          }
        >
          <div className="w-full p-4 sm:px-8 sm:py-6 bg-white">
            <div className="flex items-center">
              <div className="px-5 py-3 rounded-2xl inline-flex items-center gap-3 sm:gap-4">
                <span
                  id="enter-codes-title"
                  className="font-bold text-[#364A5E] text-lg sm:text-2xl"
                >
                  {i18n.ft(messages.enterCodes)}
                </span>
              </div>

              <SectionStatus
                status={sectionStatus}
                label={i18n.ft(messages.status[sectionStatus])}
              />
            </div>
          </div>

          <div className="p-4 sm:px-8 sm:pb-6 sm:pt-0 bg-white">
            {dimensions.map((dimension: Dimension, idx: number) => {
              const isMissingCode = missingDimensions.some(
                missingDimension => missingDimension.id === dimension.id
              );
              const hasError = completeCodesError && isMissingCode;

              return (
                <div
                  key={idx}
                  className="mb-[6px] inline-block w-full box-border rounded-[20px] bg-[#f4f8fa] px-[5px] py-3 text-base font-bold leading-6 text-neutral-700 sm:mb-[10px] sm:rounded-[20px] sm:px-5 sm:py-3 sm:leading-8"
                  style={{
                    border:
                      dimensionsCount - userScores.length < 4
                        ? showIncompleteBorder(
                            dimension.id,
                            userScores,
                            completeCodesError
                          )
                        : ''
                  }}
                >
                  <div className="grid items-center gap-3 md:grid-cols-2 md:gap-3 lg:gap-[14px]">
                    <span className="px-3 pt-3 font-bold text-[#364A5E] md:px-3 md:py-3">{`${dimension.name} (${dimension.abbreviation})`}</span>
                    <DimensionScoreInput
                      ref={element => {
                        dimensionRefs.current[dimension.id] = element;
                      }}
                      testAttemptId={testPreparationId}
                      dimensionId={dimension.id}
                      codingVideoId={Number(videoId)}
                      userAnswer={getUserAnswer(userScores, dimension.id)}
                      addUserScore={addUserScore}
                      testType={testType}
                      dimensionLabel={`${dimension.name}`}
                      hasError={hasError}
                      errorMessage={
                        hasError
                          ? getMissingDimensionError(dimension)
                          : undefined
                      }
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      </div>

      <SubmitScoreSection
        isTablet={isTablet}
        completeCodesError={completeCodesError}
        verifySubmission={() => verifySubmission()}
        testType={testType}
        missingDimensionsList={missingDimensionsList}
      />

      {!isTablet && (
        <div className="pt-2 text-center">
          <IncompleteDimensions
            completeCodesError={completeCodesError}
            missingDimensionsList={missingDimensionsList}
          />
        </div>
      )}
    </>
  );
}

export default EnterCodesSection;
